import { forwardRef, type CSSProperties } from "react";
import FbkCardShell from "./FbkCardShell";
import {
  defaultFooterLeft,
  resolveTextStyle,
  type CardFormat,
  type CardStyleSettings,
  type FactsheetCardContent,
  type FactsheetSlide,
} from "./types";

interface FactsheetCardProps {
  format: CardFormat;
  content: FactsheetCardContent;
  /** 0 = cover, 1+ = extraSlides[pageIndex - 1] */
  pageIndex?: number;
}

const FONT_BEBAS = "'Bebas Neue', 'Anton', sans-serif";
const FONT_INTER = "'Inter', system-ui, sans-serif";
const FONT_PLAYFAIR = "'Playfair Display', Georgia, serif";
const FBK_GOLD = "#FFD700";

/**
 * Per-variant readability-panel defaults, overridable per slide.
 *
 * These differ a lot by design: newsroom's whole identity is a near-solid
 * bottom panel, while cinematic wants the photo to stay dominant with just
 * enough darkening under the headline. A single global default would make
 * one of them wrong.
 *
 * `split` has no scrim at all — its text sits on solid ground, which is the
 * entire point of that layout.
 */
export const SCRIM_DEFAULTS: Record<
  FactsheetSlide["variant"],
  { strength: number; height: number }
> = {
  cinematic: { strength: 0.68, height: 0.46 },
  newsroom: { strength: 0.92, height: 0.55 },
  pill: { strength: 0.68, height: 0.46 },
  memorial: { strength: 0.95, height: 0.52 },
  split: { strength: 0, height: 0 },
};

/**
 * Wrap the FIRST occurrence of `accentWord` (case-insensitive) inside
 * `text` in a coloured span. Returns the React fragment. If no accent
 * word is supplied or no match found, returns the text unchanged.
 */
function highlightAccent(
  text: string,
  accentWord: string | undefined,
  color: string,
): React.ReactNode {
  if (!accentWord || !accentWord.trim()) return text;
  const idx = text.toLowerCase().indexOf(accentWord.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span style={{ color }}>{text.slice(idx, idx + accentWord.length)}</span>
      {text.slice(idx + accentWord.length)}
    </>
  );
}

const FactsheetCard = forwardRef<HTMLDivElement, FactsheetCardProps>(
  function FactsheetCard({ format, content, pageIndex = 0 }, ref) {
    const slides = [content.cover, ...content.extraSlides];
    const slide = slides[Math.min(pageIndex, slides.length - 1)] ?? content.cover;
    const accent = slide.accentColor || FBK_GOLD;

    // Route the slide's hero photo through the shell's `background` slot
    // instead of rendering it inside the padded main-content area. The
    // shell background is drawn at inset:0 on the ROOT (behind padding,
    // header, and footer), so the photo now fills the entire card edge-
    // to-edge — no dark padded border on any side.
    //
    // Precedence: per-slide hero image wins over any shared style.background
    // set via the Style tab (a slide is a specific piece of primary
    // content; the shared background is a fallback aesthetic).
    // Brightness now defaults to full: the scrim handles legibility, so
    // there's no longer a reason to dim the whole photo just to read text
    // that only occupies the bottom third.
    const bgOpacity = slide.imageOpacity ?? 1;
    const shellBackground = slide.imageUrl
      ? {
          imageUrl: slide.imageUrl,
          opacity: bgOpacity,
          fit: slide.imageFit ?? "cover",
          focal: slide.imageFocal ?? "center",
          blurBackdrop: true,
        }
      : content.style?.background;

    const scrimDefaults = SCRIM_DEFAULTS[slide.variant];
    const scrim = {
      strength: slide.scrimStrength ?? scrimDefaults.strength,
      height: slide.scrimHeight ?? scrimDefaults.height,
    };

    const isMemorial = slide.variant === "memorial";
    const isSplit = slide.variant === "split";
    const splitRatio = Math.min(Math.max(slide.splitRatio ?? 0.58, 0.25), 0.8);

    // Memorial variant: the footer's "Read on FBK" CTA + hashtags feel
    // promotional on a tribute, so default them to blank when the admin
    // hasn't set anything. Admin overrides still win.
    const footerLeft =
      content.footerLeft ??
      (isMemorial ? "" : defaultFooterLeft("FACTSHEET"));
    const footerRight =
      content.hashtags ?? (isMemorial ? "" : "#WC2026 · #FBK");

    return (
      <FbkCardShell
        ref={ref}
        format={format}
        footerLeft={footerLeft}
        footerRight={footerRight}
        header={content.header}
        background={shellBackground}
        // Pure black base — the photo overlay covers it entirely. Suppresses
        // the brand glow + grid so the photo can breathe.
        customBaseGradient="linear-gradient(180deg, #000 0%, #050505 100%)"
        suppressBrandOverlays
        hideFooter={content.footer?.hide}
        scrim={scrim}
        // Split draws an opaque panel over the lower part of the card so the
        // photo is confined to a clean band up top and the text below never
        // sits on imagery at all.
        fullBleedLayer={
          isSplit ? <SplitPanel ratio={splitRatio} /> : undefined
        }
      >

        {/* Layer 3 — variant body */}
        <div
          style={{
            position: "relative",
            zIndex: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {slide.variant === "cinematic" && (
            <CinematicBody
              slide={slide}
              accent={accent}
              format={format}
              style={content.style ?? {}}
            />
          )}
          {slide.variant === "newsroom" && (
            <NewsroomBody
              slide={slide}
              accent={accent}
              format={format}
              style={content.style ?? {}}
            />
          )}
          {slide.variant === "pill" && (
            <PillBody
              slide={slide}
              accent={accent}
              format={format}
              style={content.style ?? {}}
            />
          )}
          {slide.variant === "memorial" && (
            <MemorialBody
              slide={slide}
              format={format}
              style={content.style ?? {}}
            />
          )}
          {isSplit && (
            <SplitBody
              slide={slide}
              accent={accent}
              format={format}
              style={content.style ?? {}}
              ratio={splitRatio}
            />
          )}
        </div>
      </FbkCardShell>
    );
  },
);

export default FactsheetCard;

// ─── SPLIT ── image band on top, text on solid ground below ──────────────
// The scrim still puts type on top of imagery. Split removes the overlap
// entirely: the photo owns a clean band, the words own everything under it.
// Best legibility of any variant, and the only one where the photo is never
// darkened at all.

/**
 * Opaque panel covering everything below the image band. Rendered through
 * the shell's fullBleedLayer so it spans the true card box — a padded child
 * could never reach the card edges.
 */
function SplitPanel({ ratio }: { ratio: number }) {
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        top: `${ratio * 100}%`,
        bottom: 0,
        background:
          "linear-gradient(180deg, #0A0A0A 0%, #050505 55%, #000000 100%)",
        pointerEvents: "none",
      }}
    >
      {/* Thin accent seam marking the join, so the band reads as a
          deliberate edge rather than wherever the photo happened to stop. */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background:
            "linear-gradient(90deg, transparent 0%, rgba(255,215,0,0.75) 22%, rgba(255,215,0,0.75) 78%, transparent 100%)",
        }}
      />
    </div>
  );
}

function SplitBody({
  slide,
  accent,
  format,
  style,
  ratio,
}: {
  slide: FactsheetSlide;
  accent: string;
  format: CardFormat;
  style: CardStyleSettings;
  ratio: number;
}) {
  const isStory = format === "story";

  const eyebrowStyle: CSSProperties = {
    ...resolveTextStyle(
      style.eyebrow,
      {
        fontFamily: FONT_INTER,
        fontSizePx: 22,
        color: accent,
        letterSpacing: "0.45em",
        bold: true,
      },
      format,
    ),
    textTransform: "uppercase",
    marginBottom: isStory ? 16 : 11,
  };
  const headlineStyle: CSSProperties = {
    ...resolveTextStyle(
      style.headline,
      {
        fontFamily: FONT_BEBAS,
        fontSizePx: 118,
        color: "#FFFFFF",
        letterSpacing: "-0.005em",
        bold: true,
      },
      format,
    ),
    lineHeight: 0.94,
    textTransform: "uppercase",
    whiteSpace: "pre-line",
  };
  const bodyStyle: CSSProperties = {
    ...resolveTextStyle(
      style.body,
      {
        fontFamily: FONT_INTER,
        fontSizePx: 26,
        color: "rgba(255,255,255,0.82)",
        bold: false,
      },
      format,
    ),
    marginTop: isStory ? 22 : 15,
    lineHeight: 1.45,
  };

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        paddingBottom: isStory ? 40 : 26,
        // Clear the image band above. The band is measured from the card
        // top while this sits inside the padded content area, so the offset
        // is intentionally approximate — a little breathing room under the
        // seam reads better than a mathematically exact join.
        paddingTop: `${ratio * 100}%`,
      }}
    >
      {slide.eyebrow && <div style={eyebrowStyle}>{slide.eyebrow}</div>}
      <div style={headlineStyle}>
        {highlightAccent(slide.headline, slide.accentWord, accent)}
      </div>
      {slide.body && <div style={bodyStyle}>{slide.body}</div>}
    </div>
  );
}

// ─── CINEMATIC ── full-bleed photo + centered headline + accent word.
//   Closest to the "FIFA has officially CONFIRMED" reference poster.

function CinematicBody({
  slide,
  accent,
  format,
  style,
}: {
  slide: FactsheetSlide;
  accent: string;
  format: CardFormat;
  style: CardStyleSettings;
}) {
  const isStory = format === "story";
  const eyebrowStyle: CSSProperties = {
    ...resolveTextStyle(
      style.eyebrow,
      {
        fontFamily: FONT_INTER,
        fontSizePx: 22,
        color: accent,
        letterSpacing: "0.5em",
        bold: true,
        align: "center",
      },
      format,
    ),
    textTransform: "uppercase",
    marginBottom: isStory ? 20 : 14,
    textShadow: "0 2px 8px rgba(0,0,0,0.7)",
  };
  const headlineStyle: CSSProperties = {
    ...resolveTextStyle(
      style.headline,
      {
        fontFamily: FONT_BEBAS,
        fontSizePx: 168,
        color: "#FFFFFF",
        letterSpacing: "-0.01em",
        bold: true,
        align: "center",
      },
      format,
    ),
    lineHeight: 0.92,
    textTransform: "uppercase",
    textShadow: "0 6px 28px rgba(0,0,0,0.85), 0 2px 4px rgba(0,0,0,0.95)",
    whiteSpace: "pre-line",
  };
  const bodyStyle: CSSProperties = {
    ...resolveTextStyle(
      style.body,
      {
        fontFamily: FONT_INTER,
        fontSizePx: 28,
        color: "rgba(255,255,255,0.95)",
        bold: true,
        align: "center",
      },
      format,
    ),
    marginTop: isStory ? 28 : 20,
    lineHeight: 1.35,
    maxWidth: isStory ? 880 : 720,
    marginLeft: "auto",
    marginRight: "auto",
    textShadow: "0 2px 10px rgba(0,0,0,0.85)",
  };
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        paddingBottom: isStory ? 80 : 56,
      }}
    >
      {slide.eyebrow && <div style={eyebrowStyle}>{slide.eyebrow}</div>}
      <div style={headlineStyle}>
        {highlightAccent(slide.headline, slide.accentWord, accent)}
      </div>
      {slide.body && <div style={bodyStyle}>{slide.body}</div>}
    </div>
  );
}

// ─── NEWSROOM ── photo top half, headline + body in a dark bottom panel.

function NewsroomBody({
  slide,
  accent,
  format,
  style,
}: {
  slide: FactsheetSlide;
  accent: string;
  format: CardFormat;
  style: CardStyleSettings;
}) {
  const isStory = format === "story";
  const eyebrowStyle: CSSProperties = {
    ...resolveTextStyle(
      style.eyebrow,
      {
        fontFamily: FONT_INTER,
        fontSizePx: 22,
        color: accent,
        letterSpacing: "0.4em",
        bold: true,
      },
      format,
    ),
    textTransform: "uppercase",
    marginBottom: isStory ? 14 : 10,
  };
  const headlineStyle: CSSProperties = {
    ...resolveTextStyle(
      style.headline,
      {
        fontFamily: FONT_BEBAS,
        fontSizePx: 132,
        color: "#FFFFFF",
        letterSpacing: "-0.005em",
        bold: true,
      },
      format,
    ),
    lineHeight: 0.92,
    textTransform: "uppercase",
    whiteSpace: "pre-line",
  };
  const bodyStyle: CSSProperties = {
    ...resolveTextStyle(
      style.body,
      {
        fontFamily: FONT_INTER,
        fontSizePx: 26,
        color: "rgba(255,255,255,0.85)",
        bold: false,
      },
      format,
    ),
    marginTop: isStory ? 26 : 18,
    lineHeight: 1.4,
    maxWidth: isStory ? 920 : 760,
  };
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        paddingBottom: isStory ? 60 : 40,
      }}
    >
      {/* Thin gold rule above the typography panel — broadcast lower-third */}
      <div
        style={{
          height: 3,
          background: accent,
          width: isStory ? 120 : 90,
          marginBottom: isStory ? 28 : 20,
        }}
      />
      {slide.eyebrow && <div style={eyebrowStyle}>{slide.eyebrow}</div>}
      <div style={headlineStyle}>
        {highlightAccent(slide.headline, slide.accentWord, accent)}
      </div>
      {slide.body && <div style={bodyStyle}>{slide.body}</div>}
    </div>
  );
}

// ─── PILL ── full-bleed photo, small "RULE 02 / 09" pill above the headline.

function PillBody({
  slide,
  accent,
  format,
  style,
}: {
  slide: FactsheetSlide;
  accent: string;
  format: CardFormat;
  style: CardStyleSettings;
}) {
  const isStory = format === "story";
  const eyebrowStyle: CSSProperties = {
    ...resolveTextStyle(
      style.eyebrow,
      {
        fontFamily: FONT_INTER,
        fontSizePx: 22,
        color: "rgba(255,255,255,0.95)",
        letterSpacing: "0.4em",
        bold: true,
        align: "center",
      },
      format,
    ),
    textTransform: "uppercase",
    marginBottom: isStory ? 14 : 10,
    textShadow: "0 2px 8px rgba(0,0,0,0.8)",
  };
  const headlineStyle: CSSProperties = {
    ...resolveTextStyle(
      style.headline,
      {
        fontFamily: FONT_BEBAS,
        fontSizePx: 152,
        color: "#FFFFFF",
        letterSpacing: "-0.01em",
        bold: true,
        align: "center",
      },
      format,
    ),
    lineHeight: 0.92,
    textTransform: "uppercase",
    whiteSpace: "pre-line",
    textShadow: "0 6px 28px rgba(0,0,0,0.85), 0 2px 4px rgba(0,0,0,0.95)",
  };
  const bodyStyle: CSSProperties = {
    ...resolveTextStyle(
      style.body,
      {
        fontFamily: FONT_INTER,
        fontSizePx: 26,
        color: "rgba(255,255,255,0.92)",
        bold: true,
        align: "center",
      },
      format,
    ),
    marginTop: isStory ? 24 : 18,
    lineHeight: 1.35,
    maxWidth: isStory ? 880 : 720,
    textShadow: "0 2px 10px rgba(0,0,0,0.85)",
  };
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        alignItems: "center",
        paddingBottom: isStory ? 80 : 56,
      }}
    >
      {slide.pillLabel && (
        <div
          style={{
            display: "inline-block",
            padding: `${isStory ? 10 : 8}px ${isStory ? 24 : 18}px`,
            borderRadius: 9999,
            background: accent,
            color: "#0A0A0A",
            fontFamily: FONT_BEBAS,
            fontSize: isStory ? 22 : 17,
            fontWeight: 800,
            letterSpacing: "0.3em",
            textTransform: "uppercase",
            marginBottom: isStory ? 24 : 18,
            boxShadow: "0 8px 22px rgba(0,0,0,0.55)",
          }}
        >
          {slide.pillLabel}
        </div>
      )}
      {slide.eyebrow && <div style={eyebrowStyle}>{slide.eyebrow}</div>}
      <div style={headlineStyle}>
        {highlightAccent(slide.headline, slide.accentWord, accent)}
      </div>
      {slide.body && <div style={bodyStyle}>{slide.body}</div>}
    </div>
  );
}

// ─── MEMORIAL ── quiet tribute: full-bleed portrait, serif headline, dates
//   in small caps, optional body line. All type in white / off-white — no
//   coloured accent. Footer/hashtags default to blank (see the CTA-strip
//   logic upstream) so nothing feels promotional on a memorial post.

function MemorialBody({
  slide,
  format,
  style,
}: {
  slide: FactsheetSlide;
  format: CardFormat;
  style: CardStyleSettings;
}) {
  const isStory = format === "story";
  const eyebrowStyle: CSSProperties = {
    ...resolveTextStyle(
      style.eyebrow,
      {
        fontFamily: FONT_INTER,
        fontSizePx: 20,
        color: "rgba(255,255,255,0.85)",
        letterSpacing: "0.6em",
        bold: false,
        align: "center",
      },
      format,
    ),
    textTransform: "uppercase",
    marginBottom: isStory ? 22 : 14,
    textShadow: "0 2px 8px rgba(0,0,0,0.7)",
  };
  const headlineStyle: CSSProperties = {
    ...resolveTextStyle(
      style.headline,
      {
        fontFamily: FONT_PLAYFAIR,
        fontSizePx: 148,
        color: "#FFFFFF",
        letterSpacing: "-0.005em",
        bold: true,
        align: "center",
      },
      format,
    ),
    lineHeight: 1.0,
    textShadow: "0 6px 28px rgba(0,0,0,0.85), 0 2px 4px rgba(0,0,0,0.95)",
    whiteSpace: "pre-line",
  };
  // tagline slot styles the dates line for memorial.
  const datesStyle: CSSProperties = {
    ...resolveTextStyle(
      style.tagline,
      {
        fontFamily: FONT_INTER,
        fontSizePx: 26,
        color: "rgba(255,255,255,0.92)",
        letterSpacing: "0.35em",
        bold: true,
        align: "center",
      },
      format,
    ),
    textTransform: "uppercase",
    textShadow: "0 2px 10px rgba(0,0,0,0.8)",
  };
  const bodyStyle: CSSProperties = {
    ...resolveTextStyle(
      style.body,
      {
        fontFamily: FONT_PLAYFAIR,
        fontSizePx: 24,
        color: "rgba(255,255,255,0.82)",
        italic: true,
        bold: false,
        align: "center",
      },
      format,
    ),
    marginTop: isStory ? 28 : 20,
    lineHeight: 1.4,
    maxWidth: isStory ? 820 : 640,
    textShadow: "0 2px 10px rgba(0,0,0,0.75)",
  };
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        alignItems: "center",
        paddingBottom: isStory ? 60 : 40,
      }}
    >
      {slide.eyebrow && <div style={eyebrowStyle}>{slide.eyebrow}</div>}
      <div style={headlineStyle}>{slide.headline}</div>
      {/* Thin off-white horizontal rule between name and dates */}
      <div
        aria-hidden
        style={{
          width: isStory ? 220 : 160,
          height: 1,
          background: "rgba(255,255,255,0.65)",
          marginTop: isStory ? 26 : 18,
          marginBottom: isStory ? 20 : 14,
        }}
      />
      {slide.datesLine && <div style={datesStyle}>{slide.datesLine}</div>}
      {slide.body && <div style={bodyStyle}>{slide.body}</div>}
    </div>
  );
}
